'use strict';

bbw.service.chat.Client = Class({

    WAIT_QUEUE_TIMEOUT: 30000,
    SEND_ATTEMPTS_COUNT_LIMIT: 10,
    CONNECT_RETRY_TIMEOUT: 5000,

    $q: undefined,

    _userId: undefined,
    _userIsLocked: undefined,
    _userAvatar: undefined,

    /**
     * @type {ngtk.rpcClient.Client}
     */
    _rpcClient: undefined,

    /**
     * @type {bbw.service.chat.WebSocketClient}
     */
    _wsClient: undefined,

    _isAuthenticated: false,

    _isConnected: false,
    _isStopped: false,

    _connecting: false,

    _allocateChatTokenDefer: undefined,
    _connectDefer: undefined,

    /**
     * @type {Array}
     */
    _sendQueue: null,

    /**
     * @type {Array}
     */
    _waitQueue: null,

    _cbOnMessageSent: undefined,
    _cbOnMessageReceived: undefined,
    _cbOnConnected: undefined,

    _subContacts: false,
    _subChats: {},

    construct: function($q, rpcClient, wsClient) {
        this.$q = $q;
        this._rpcClient = rpcClient;
        this._wsClient = wsClient.setHandler(this);
        this._setupTimeoutPolling();
    },

    getAvatar: function() {
        return this._userId ? this._userAvatar : undefined;
    },

    onMessageSent: function(cbOnMessageSent) {
        this._cbOnMessageSent = cbOnMessageSent;
    },

    onConnected: function(cbOnConnected) {
        this._cbOnConnected = cbOnConnected;
    },

    onMessageReceived: function(cbOnMessageReceived) {
        this._cbOnMessageReceived = cbOnMessageReceived;
    },

    start: function() {
        if (this._isConnected) {
            return this.$q.when(true);
        }
        this._sendQueue = [];
        return this._connect();
    },

    _subscribeAfterConnect: function() {
        var promises = [];

        if (this._subContacts) {
            promises.push(this.subscribeContactsUpdate());
        }

        Object.keys(this._subChats).forEach(function(id) {
            promises.push(this.subscribeChat(id));
        }.bind(this));

        return this.$q.all(promises);
    },

    setSubscribeContacts: function(value) {
        value = angular.isDefined(value) ? !!value : true;
        var is_changed = this._subContacts != value;
        this._subContacts = value;
        return is_changed;
    },

    setSubscribeChat: function(id, value) {
        var is_changed;
        if (angular.isUndefined(value) || value) {
            is_changed = !this._subChats[id];
            this._subChats[id] = true;
        } else {
            is_changed = this._subChats[id];
            delete this._subChats[id];
        }
        return is_changed;
    },

    _getValidatedUserId: function(from_user_id) {
        var id = parseInt(from_user_id);
        if (id < 1 || isNaN(id)) {
            throw new Error('Invalid argument: ' + from_user_id);
        }
        return id;
    },

    subscribeContactsUpdate: function() {
        this.setSubscribeContacts(true);
        return this.send(['subscribeContactsUpdate'], {});
    },

    unsubscribeContactsUpdate: function() {
        return this.setSubscribeContacts(false)
            ? this.send(['subscribeContactsUpdate'], {unsubscribe: true})
            : this.$q.when(true);
    },

    subscribeChat: function(from_user_id) {
        var id = this._getValidatedUserId(from_user_id);
        this.setSubscribeChat(id);
        return this.send(['subscribeChat'], {from_user_id: id});
    },

    unsubscribeChat: function(from_user_id) {
        var id = this._getValidatedUserId(from_user_id);
        return this.setSubscribeContacts(id, false)
            ? this.send(['subscribeChat'], {from_user_id: id, unsubscribe: true})
            : this.$q.when(true);
    },

    unsubscribeAllChats: function() {
        var user_ids = Object.keys(this._subChats);
        if (!user_ids.length) {
            return this.$q.when(true);
        }
        return this.$q.all(user_ids.map(this.unsubscribeChat.bind(this)));
    },

    markRead: function(from_user_id, last_serial) {
        return this.send('markRead', {from_user_id: from_user_id, last_serial: last_serial});
    },

    sendChatMessage: function(conversationMessage) {
        if (!(conversationMessage instanceof bbw.model.ConversationMessage)) {
            throw new Error("Invalid argumnent: not an instance of ConversationMessage");
        }
        if (conversationMessage.status != bbw.model.ConversationMessage.STATUS_NEW) {
            throw new Error("Not a new message");
        }
        var args = conversationMessage.prepareAndGetJsonToSend(this.getUserId());
        return this.send('send', args);
    },

    getUserId: function() {
        return this._userId;
    },

    isUserLocked: function() {
        return this._userIsLocked;
    },

    isConnecting: function() {
        return this._connecting;
    },

    _connect: function() {
        if (!this.isSupported()) {
            return this.$q.reject('websockets_not_supported');
        }
        if (this._isStopped || bbw.userIsLocked) {
            return this.$q.when(true);
        }
        return this._doConnect().catch(
            function() {
                if (!this._isStopped) {
                    this._connecting = true;
                    setTimeout(this._doConnect.bind(this), this.CONNECT_RETRY_TIMEOUT);
                }
            }.bind(this));
    },

    _doConnect: function() {
        if (!this._connectDefer) {
            this._connectDefer = this.$q.defer();
        }
        this._isConnected = false;
        this._connecting = true;
        this._isAuthenticated = false;
        this._waitQueue = [];
        this._allocateChatTokenDefer = undefined;
        this.
            allocateChatToken().then(function(response) {
                if (!response.token || !response.user_id) {
                    return this.$q.reject('internal_error');
                }
                this._userId = response.user_id;
                this._userIsLocked = response.is_locked;
                this._userAvatar = response.avatar;
                return response.token;
            }.bind(this)).
            then(this._startWs.bind(this)).
            then(function() {
                this._connecting = false;
            }.bind(this)).
            then(this.send.bind(this, ['ping'])).
            then(this._subscribeAfterConnect.bind(this)).
            then(this._sendQueuedMessages.bind(this)).
            then(function(result) {
                this._isConnected = true;
                if (this._connectDefer) {
                    this._connectDefer.resolve(result);
                    this._connectDefer = undefined;
                }
                if (this._cbOnConnected) {
                    this._cbOnConnected();
                }
            }.bind(this)).catch(angular.noop);

        return this._connectDefer.promise;
    },

    _sendQueuedMessages: function() {
        var message;

        if (this._connecting || this._isStopped) {
            return;
        }

        while (message = this._sendQueue.shift()) {
            if (message.send_attempts_count > this.SEND_ATTEMPTS_COUNT_LIMIT) {
                this._onMessageFailed(message);
                continue;
            }
            if (this._sendWsMessage(message)) {
                if (message.id) {
                    this._waitQueue.push(message);
                }
            } else {
                message.no_retry || this._sendQueue.unshift(message);
                this._connect();
                break;
            }
        }
    },

    _onMessageFailed: function(message) {
        console.error('message failed:', message);
        message.rejectPromise('failed');
    },

    allocateChatToken: function() {
        if (!this._allocateChatTokenDefer) {
            this._allocateChatTokenDefer = this.$q.defer();
        }

        this._apiAlocateChatToken().then(function(response) {
            if (this._allocateChatTokenDefer) {
                this._allocateChatTokenDefer.resolve(response);
            }
            this._allocateChatTokenDefer = undefined;
        }.bind(this)).catch(function(error) {
            console.error('Could not allocateChatToken, retrying in ' + this.CONNECT_RETRY_TIMEOUT, error);
            setTimeout(this.allocateChatToken.bind(this), this.CONNECT_RETRY_TIMEOUT);
        }.bind(this));

        return this._allocateChatTokenDefer.promise;
    },

    _apiAlocateChatToken: function() {
        return this._rpcClient.get('chat/allocate-token', {}).execute();
    },

    notify: function(method, args) {
        this.send(method, args, null);
    },

    send: function(method, args, id) {
        var promise, no_retry = false;
        if (angular.isArray(method)) {
            method = method[0];
            no_retry = true;
        }
        var msg = this._createMessage(method, args, id);
        if (no_retry) {
            msg.no_retry = true;
        }
        if (msg.id) {
            promise = msg.createPromise(this.$q);
        } else {
            promise = this.$q.when(true);
        }
        this._sendQueue.push(msg);
        this._sendQueuedMessages();
        return promise;
    },

    _createMessage: function(method, args, id) {
        return this._wsClient.createMessage(method, args, id);
    },

    _startWs: function(token) {
        this._isAuthenticated = false;
        var authMessage = this._wsClient.createMessage('auth', {
            token: token,
            user_id: this._userId
        });
        return this._wsClient.connect(authMessage).then(function() {
            this._isAuthenticated = true;
        }.bind(this));
    },

    _sendWsMessage: function(message) {
        return !!(this._isAuthenticated && this._wsClient.send(message.prepareToSend()));
    },

    onWsMessage: function(message) {
        if (message.id) {
            var queryMessage = this._findPairMessage(message);
            if (queryMessage) {
                queryMessage.resolvePromise(message, queryMessage);
                return;
            }
        }
        if (angular.isFunction(this._cbOnMessageReceived)) {
            this._cbOnMessageReceived(message);
        }
    },

    _findPairMessage: function(msg) {
        if (!msg.id) {
            return undefined;
        }
        return this._scanMessages(function(waitQueueMsg) {
            return waitQueueMsg.isPairTo(msg);
        });
    },

    _setupTimeoutPolling: function() {
        setInterval(
            function() {
                if (this._connecting || this._isStopped) {
                    return;
                }
                if (this._isAuthenticated) {
                    this.notify('ping');
                }
                if (this._waitQueue) {
                    this._scanMessages();
                }
            }.bind(this),
            Math.ceil(this.WAIT_QUEUE_TIMEOUT / 2)
        );
    },

    _scanMessages: function(cb) {
        var resultIdx = undefined;
        for (var i = 0; i < this._waitQueue.length; ++i) {
            var waitQueueMsg = this._waitQueue[i];
            if (angular.isUndefined(resultIdx) && angular.isFunction(cb) && cb(waitQueueMsg)) {
                resultIdx = i;
            } else if (waitQueueMsg.isTimedOut(this.WAIT_QUEUE_TIMEOUT)) {
                this._onMessageFailed(waitQueueMsg);
                this._waitQueue.splice(i, 1);
            }
        }
        if (angular.isDefined(resultIdx)) {
            return this._waitQueue.splice(resultIdx, 1)[0];
        } else {
            return undefined;
        }
    },

    isStopped: function() {
        return this._isStopped;
    },

    isSupported: function() {
        return angular.isDefined(window.WebSocket);
    },

    stop: function() {
        if (!this._isStopped) {
            console.warn('ChatClient STOP');
            this._isStopped = true;
            this._isConnected = false;
            this._ws && this._ws.disconnect();
        }
    },

    onWsClose: function() {
        console.warn('ChatClient onWsClose');
        this._isConnected = false;
        if (!this._isStopped) {
            console.info('ChatClient reconnect');
            this._connect();
        }
    }

});
